import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { InvoiceStatus } from '../../types';

interface BulkUpdateDialogProps {
  open: boolean;
  selectedCount: number;
  status: InvoiceStatus;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (status: InvoiceStatus) => void;
  onSubmit: (reviewerComments?: string) => void;
  isSubmitting?: boolean;
  userRole?: string;
}

export const BulkUpdateDialog: React.FC<BulkUpdateDialogProps> = ({
  open,
  selectedCount,
  status,
  onOpenChange,
  onStatusChange,
  onSubmit,
  isSubmitting = false,
  userRole,
}) => {
  const { t } = useTranslation('invoices');
  const { t: tCommon } = useTranslation('common');
  const [reviewerComments, setReviewerComments] = useState('');

  useEffect(() => {
    if (!open) {
      setReviewerComments('');
    }
  }, [open]);

  const showCommentsField = status === InvoiceStatus.UNDER_REVIEW || status === InvoiceStatus.REJECTED;

  const handleSubmit = () => {
    const comments = showCommentsField && reviewerComments.trim() ? reviewerComments.trim() : undefined;
    onSubmit(comments);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('bulkUpdate.title')}</DialogTitle>
          <DialogDescription>
            {t('bulkUpdate.description', { count: selectedCount })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="bulk-status">{t('bulkUpdate.newStatus')}</Label>
            <Select value={status} onValueChange={(value) => onStatusChange(value as InvoiceStatus)}>
              <SelectTrigger id="bulk-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={InvoiceStatus.PENDING}>{tCommon('status.pending')}</SelectItem>
                <SelectItem value={InvoiceStatus.UNDER_REVIEW}>{tCommon('status.underReview')}</SelectItem>
                <SelectItem value={InvoiceStatus.APPROVED}>{tCommon('status.approved')}</SelectItem>
                <SelectItem value={InvoiceStatus.REJECTED}>{tCommon('status.rejected')}</SelectItem>
                {userRole === 'admin' && (
                  <SelectItem value={InvoiceStatus.PAID}>{tCommon('status.paid')}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-muted rounded-lg p-3 text-sm">
            <p className="font-medium">{t('bulkUpdate.warning')}:</p>
            <p className="text-muted-foreground mt-1">
              {t('bulkUpdate.warningMessage', { count: selectedCount, status })}
            </p>
          </div>

          {showCommentsField && (
            <div className="grid gap-2">
              <Label htmlFor="bulk-reviewer-comments">{t('bulkUpdate.reviewerComments')}</Label>
              <Textarea
                id="bulk-reviewer-comments"
                rows={3}
                placeholder={t('bulkUpdate.reviewerCommentsPlaceholder')}
                value={reviewerComments}
                onChange={(e) => setReviewerComments(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('bulkUpdate.reviewerCommentsHint')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {tCommon('buttons.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? tCommon('loading') : t('bulkUpdate.updateAll')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
